import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  FileText, 
  PlusCircle, 
  BarChart3, 
  Users, 
  LogOut, 
  Globe,
  Pill,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Package,
  Phone
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isAnyAdmin } = useAuth();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isInventoryAdmin = () => user?.role === 'inventory_admin';

  const navItems = [
    ...(!isInventoryAdmin() ? [
      { path: '/dashboard', icon: LayoutDashboard, label: 'dashboard' },
      { path: '/new-receipt', icon: PlusCircle, label: 'new_receipt' },
      { path: '/receipts', icon: FileText, label: 'receipts_list' },
    ] : []),
    ...(isAnyAdmin() ? [
      { path: '/statistics', icon: BarChart3, label: 'statistics' },
      { path: '/reports', icon: ClipboardList, label: 'reports_title' },
    ] : []),
    ...((isAnyAdmin() || isInventoryAdmin()) ? [
      { path: '/inventory', icon: Package, label: 'inventory' },
    ] : []),
    ...(isAnyAdmin() ? [
      { path: '/users', icon: Users, label: 'users_management' },
    ] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-white border-e border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md">
                <Pill className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900 text-sm leading-tight">
                  {language === 'ar' ? 'توثيق دواء X' : 'Med X Docs'}
                </h1>
                <span className="text-xs text-slate-500">
                  {language === 'ar' ? 'نظام التتبع' : 'Tracking System'}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-150 ${
                    active 
                      ? 'bg-sky-50 text-sky-600 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  data-testid={`nav-${item.label}`}
                >
                  <Icon className={`h-5 w-5 ${active ? 'text-sky-500' : ''}`} />
                  <span>{t(item.label)}</span>
                </Link>
              );
            })}
          </nav>

          {/* Support Contact */}
          <div className="px-4 py-3 border-t border-slate-100">
            <a 
              href="tel:33180186" 
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-sky-50 hover:text-sky-600 transition-colors"
            >
              <Phone className="h-4 w-4 text-sky-500" />
              <div>
                <span className="text-sm font-medium">{t('support_contact')}</span>
                <span className="block text-xs text-slate-400 font-mono" dir="ltr">33180186</span>
              </div>
            </a>
          </div>

          {/* User info & Logout */}
          <div className="p-4 border-t border-slate-100">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">
                {user?.role === 'admin' ? t('admin') : user?.role === 'admin_role' ? t('admin_role') : user?.role === 'inventory_admin' ? t('inventory_admin') : t('receptionist')}
              </p>
            </div>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="w-full justify-start gap-2 rounded-lg"
                data-testid="sidebar-language-toggle"
              >
                <Globe className="h-4 w-4" />
                {language === 'ar' ? 'English' : 'العربية'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start gap-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between px-4 py-3 lg:px-8">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex-1" />
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                {t('welcome')}, {user?.full_name}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="gap-2 rounded-lg text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="header-logout-button"
              >
                <LogOut className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                {t('logout')}
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
